/* assets/frame-addon.js
 * Frame add-on: reads the #frame-catalog-json / #frame-i18n-json blobs emitted by
 * snippets/frame-picker.liquid, derives one selectable "tile" per frame colour, builds and
 * wires the tile buttons, and exposes window.TBPFrame so any add-to-cart flow (poster
 * builder JS, buy-buttons, etc.) can read the current selection and build the two linked
 * /cart/add.js line items.
 *
 * Plain vanilla IIFE, no framework, no build step -- mirrors the DOM/logic split used by
 * assets/flower-poster-section.js. Degrades silently: with no picker on the page, TBPFrame
 * still exists and buildItems(x) returns [x] unchanged.
 */
(function () {
  "use strict";

  var FRAME_VERSION = "v2026-08-30.1";

  // buy-buttons.liquid (which mounts the picker) is rendered by 8 sections, so a page
  // combining e.g. main-product with a featured-product block can end up with two
  // <script src="frame-addon.js" defer> tags -- each a separate <script> element, so each
  // genuinely re-executes this IIFE (the browser does not dedupe execution by URL). A second
  // execution must be a no-op: it would otherwise build a second, disconnected STATE/listener
  // closure and clobber window.TBPFrame with a controller whose tiles were already claimed
  // (see the per-root dataset guard below) -- silently breaking every already-registered
  // onChange listener and any reference already held to the first (correct) controller.
  if (window.TBPFrame && window.TBPFrame.VERSION === FRAME_VERSION) return;

  // ============================================================
  // A) CATALOG + I18N (pure data, no DOM)
  //
  // The catalogue's frame products come in two shapes and this file supports both with one
  // code path:
  //   - one product per colour, each with a single size option ("legacy" shape), or
  //   - one product carrying BOTH a colour option and a size option, so its variants are the
  //     colour x size cross product (e.g. "Svart / 50x70", "Ek / 50x70", ...).
  // Liquid emits the RAW per-variant option1/option2/option3 values; this file is the only
  // place that decides which option is "size" (by pattern-matching its VALUES, since the
  // option could be named "Storlek"/"Size"/"Größe" depending on market) and groups variants
  // into one tile per distinct colour value.
  // ============================================================
  function readJSON(id) {
    try {
      var el = document.getElementById(id);
      return JSON.parse((el && el.textContent) || "{}") || {};
    } catch (e) { return {}; }
  }

  var CATALOG_DATA = readJSON("frame-catalog-json");
  var CATALOG = Array.isArray(CATALOG_DATA.products) ? CATALOG_DATA.products : [];
  var MONEY_FORMAT = CATALOG_DATA.moneyFormat || null;

  // A poster whose variants carry no detectable size (findSizeOptionIndex-equivalent check,
  // done storefront-side by product-form.js's getSizeFromVariant against a regex) has NO other
  // way to tell this picker what size it is -- 2026-08-30: a single-size poster
  // ("fodelsetavla-skala-1-1", sold only as 50x70 with no size option at all) hit exactly this
  // gap and resolveForSize's old "just guess the frame's first size" fallback silently sold it
  // a 30x40 frame. The fix for THAT bug (below) is to never guess; the fix for THIS gap is a
  // product tag "frame:size:50x70" as an explicit, storefront-supplied default.
  //
  // A tag is normally banned from customer-facing data on this picker (see frame-picker.liquid's
  // "Label source" comment: tags aren't translatable, and this store runs sv/en/de). A SIZE is
  // the one exception -- "50x70" is identical in every market, it's never displayed as
  // translated prose (only substituted into a note via {frame_price}/{poster_price}-style
  // placeholders and matched by normSize), so nothing here needs translation. Do not "fix" this
  // by removing it for consistency with the label rule; the label rule is about prose, this is
  // about a dimension.
  var FRAME_SIZE_TAG_PREFIX = "frame:size:";
  var DEFAULT_SIZE = (function () {
    var tags = Array.isArray(CATALOG_DATA.tags) ? CATALOG_DATA.tags : [];
    for (var i = 0; i < tags.length; i++) {
      var tag = String(tags[i] || "");
      if (tag.indexOf(FRAME_SIZE_TAG_PREFIX) === 0) return tag.slice(FRAME_SIZE_TAG_PREFIX.length);
    }
    return "";
  })();

  var I18N = readJSON("frame-i18n-json");
  function t(key, fallback) {
    var v = I18N[key];
    return (typeof v === "string" && v) ? v : fallback;
  }

  // Canonicalise a size value to "<w>x<h>" so a frame variant's size matches a poster size
  // however either side typed it -- "30x40", "30×40", "30 × 40 cm" and "30X40cm" (the
  // client's real frame variants carry a " cm" unit suffix the poster's own size string
  // does not) must all collapse to the SAME key. Extract the dimension pair with a regex
  // rather than just stripping characters (the bug this replaced: stripping "x"/"×"/
  // whitespace but not "cm" left "30x40 cm" as "3040cm" while the poster's "30x40" became
  // "3040" -- two different keys for the same size, so the old exact-match sizeMap lookup
  // resolved nothing and every frame showed unavailable at every size). Falls back to the
  // old strip-everything behaviour for a non-dimensional value so unrelated option values
  // still key consistently.
  function normSize(s) {
    var str = String(s || "").toLowerCase().trim();
    var m = str.match(/(\d+(?:[.,]\d+)?)\s*[x×]\s*(\d+(?:[.,]\d+)?)/);
    if (m) return Math.round(parseFloat(m[1].replace(",", "."))) + "x" + Math.round(parseFloat(m[2].replace(",", ".")));
    return str.replace(/[×x\s]/g, "");
  }

  // NOTE -- deliberately duplicated: assets/cart-frame-link.js has its own copy of this
  // same size-vs-colour detection (its own buildCatalogTiles()), for the cart's "add a
  // frame" upsell row. Loading this file on the cart page too, just for this one helper,
  // isn't worth a second script tag on the store's most conversion-critical page (see the
  // 2026-08-05 conversion audit's first-paint finding). A fix to the size-detection regex
  // or the colour/size grouping logic in ONE of these two files must be applied to the other.
  // normSize (above) is mirrored there too -- it's the function that broke when the client's
  // real frame variants turned out to carry a " cm" unit suffix; a future fix to its
  // canonicalisation needs the same mirrored update.

  // A value "looks like a size" if it contains a <number><x|×><number> pattern anywhere --
  // "50x70", "30 x 40 cm", "50×70" all match. Never test the option's NAME, only its values.
  var SIZE_VALUE_RE = /\d+\s*[x×]\s*\d+/i;
  function looksLikeSize(v) { return SIZE_VALUE_RE.test(v == null ? "" : String(v)); }

  function optionValue(variant, idx) { return variant["option" + (idx + 1)]; }

  // Which option index (0/1/2 -> option1/2/3) holds size values, found by inspecting every
  // variant's value at that index -- not by option name. -1 if none of the product's options
  // look size-like (e.g. a colour-only product with no size differentiation at all).
  function findSizeOptionIndex(product) {
    var variants = product.variants || [];
    var n = product.optionsCount || 1;
    for (var idx = 0; idx < n; idx++) {
      var saw = false, allSize = true;
      for (var i = 0; i < variants.length; i++) {
        var v = optionValue(variants[i], idx);
        if (v == null || v === "") continue;
        saw = true;
        if (!looksLikeSize(v)) { allSize = false; break; }
      }
      if (saw && allSize) return idx;
    }
    return -1;
  }

  // One tile per distinct colour value on a product (or one tile for the whole product when
  // it has no separate colour option -- the legacy one-product-per-colour shape, or a
  // colour-only product with a single size option). sizeMap is normSize(size) -> the resolved
  // {id, price, priceFormatted, available} for that size, so setSize() becomes a direct
  // lookup instead of a substring scan -- the substring scan is exactly what caused the
  // cross-binding bug on a two-option product (every colour's "50x70" variant title contains
  // "5070", so a title-substring match binds whichever colour happens to be first).
  function makeTile(product, label, variants, sizeIdx) {
    var sizeMap = Object.create(null);
    var hasOwnImage = false;
    variants.forEach(function (v) {
      var sizeValue = sizeIdx === -1 ? "" : optionValue(v, sizeIdx);
      sizeMap[normSize(sizeValue)] = {
        id: v.id,
        price: v.price,
        priceFormatted: v.priceFormatted,
        // frames are made to order, never stock-limited: a frame at a matching size is always offered
        available: true
      };
      if (v.image) hasOwnImage = true;
    });
    var image = null;
    for (var i = 0; i < variants.length; i++) {
      if (variants[i].image) { image = variants[i].image; break; }
    }
    if (!image) image = product.image || null;
    return {
      key: product.handle + "::" + label,
      label: label,
      image: image,
      sizeMap: sizeMap,
      hasOwnImage: hasOwnImage
    };
  }

  function buildTilesFromCatalog(products) {
    var tiles = [];
    (products || []).forEach(function (product) {
      var variants = product.variants || [];
      var sizeIdx = findSizeOptionIndex(product);
      var n = product.optionsCount || 1;
      var colorIdx = -1;
      for (var i = 0; i < n; i++) {
        if (i !== sizeIdx) { colorIdx = i; break; }
      }

      if (colorIdx === -1) {
        // No distinct colour option: one tile for the whole product.
        tiles.push(makeTile(product, product.labelOverride || product.title, variants, sizeIdx));
        return;
      }

      // Group this product's variants by their colour-option value -- one tile per distinct
      // colour (e.g. "Svart"/"Ek"/"Vit" on a single multi-option product), in first-seen order.
      var order = [];
      var groups = Object.create(null);
      variants.forEach(function (v) {
        var color = optionValue(v, colorIdx);
        if (color == null || color === "") return;
        if (!groups[color]) { groups[color] = []; order.push(color); }
        groups[color].push(v);
      });
      var productTiles = order.map(function (color) { return makeTile(product, color, groups[color], sizeIdx); });
      // The client asked for real per-colour photography; a multi-colour product whose
      // variants carry no image of their own would render every one of its tiles with the
      // same (product-level) photo, which looks broken rather than "no frame added yet".
      // Surface it loudly rather than silently shipping identical thumbnails.
      if (productTiles.length > 1 && !productTiles.some(function (tl) { return tl.hasOwnImage; })) {
        console.warn("[TBPFrame] product \"" + product.handle + "\" has " + productTiles.length +
          " colours but no per-variant images -- all its tiles will show the same photo. " +
          "Assign an image to at least one variant per colour in Shopify.");
      }
      tiles = tiles.concat(productTiles);
    });
    return tiles;
  }

  var TILES = buildTilesFromCatalog(CATALOG);

  function findTileByKey(key) {
    if (!key) return undefined;
    return TILES.find(function (tl) { return tl.key === key; });
  }

  // Resolve a tile's variant for a given size: a direct sizeMap lookup, never a substring scan
  // (see the cross-binding note on makeTile), and -- as of the 2026-08-30 mis-sell fix -- never
  // a guess either. This USED to fall back to the tile's first size entry (in insertion order)
  // when `size` was blank, "purely so the initial paint has something to show". That's exactly
  // what silently sold a 30x40 frame against a poster that only exists as 50x70: with no size
  // known, every colour's "first" size is arbitrary and has no relationship to the poster in
  // the customer's cart. An unresolvable size must disable the tile (renderTiles already does
  // this via `!resolved`), never substitute a different one. See DEFAULT_SIZE above and
  // setSize() below for the (safe, explicit) way a size-less poster can still offer a frame.
  function resolveForSize(tile, size) {
    if (!tile) return undefined;
    var map = tile.sizeMap || {};
    var want = normSize(size);
    if (!want) return undefined;
    return map[want];
  }

  function composeForLabel(title, size) {
    var a = String(title || "").trim();
    var b = String(size || "").trim();
    if (a && b) return a + " " + b;
    return a || b;
  }

  function randomBundleId() {
    return "b_" + Math.random().toString(36).slice(2, 8);
  }

  // Fill the shop's own money_format string (e.g. amount_no_decimals in moustache braces,
  // then " kr") so a client-computed total (poster + frame, which has no server-rendered
  // priceFormatted of its own) matches the "X kr" style every other price on the page already
  // uses, instead of falling back to a bare ISO code. Returns null if the format has no
  // placeholder we recognize, so the caller can fall back to its own plain rendering.
  // Copied from assets/flower-poster-section.js's proven implementation -- keep the two in
  // sync if either needs a fix.
  function applyMoneyFormat(cents, format) {
    var units = cents / 100;
    var noDecimals = Math.round(units).toString();
    var withDecimals = units.toFixed(2);
    var commaDecimals = withDecimals.replace(".", ",");
    var raw = String(format);
    var substituted = raw
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, commaDecimals)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals)
      .replace(/\{\{\s*amount\s*\}\}/g, withDecimals);
    if (substituted === raw) return null; // no known placeholder -- let the caller fall back
    return substituted.replace(/<[^>]+>/g, ""); // money_format sometimes wraps in a <span>
  }

  // ============================================================
  // B) STATE
  // ============================================================
  var STATE = {
    selectedKey: null, // null = "no frame"
    // DEFAULT_SIZE (from the frame:size:* tag) seeds this so a size-less poster still resolves
    // tiles from first paint, before any setSize() call ever arrives -- "" when untagged, which
    // renderTiles/resolveForSize correctly treat as "unknown, disable".
    size: DEFAULT_SIZE,
    // Cents, from the poster's own selected_or_first_available_variant.price at boot, kept in
    // sync by product-form.js's onVariantIdChanged -> setPosterPrice() as the customer changes
    // variant. 0/falsy means "unknown" -- renderNote() must fall back to the plain
    // selected_note rather than show a total built from a missing price.
    posterPrice: Number(CATALOG_DATA.posterPrice) || 0
  };

  var listeners = [];
  function notify() {
    var selection = getSelection();
    listeners.forEach(function (cb) {
      try { cb(selection); } catch (e) { /* one bad listener must not break the rest */ }
    });
  }

  // ============================================================
  // C) DOM (no-ops throughout when the picker isn't on the page; binds every
  // [data-frame-picker] instance found, in case more than one is mounted -- see the
  // module-guard comment above. Each instance gets its own DOM copy of the derived TILES
  // (appended into its own [data-frame-tiles] slot), but there is one shared STATE/selection
  // (the public API takes no instance handle), so every instance always mirrors the same
  // selection rather than tracking its own. A per-root dataset guard (the repo's own idiom
  // -- see flower-poster-section.js's dataset.fbBooted) stops a root from ever being bound
  // twice.
  // ============================================================
  var roots = Array.prototype.slice.call(document.querySelectorAll("[data-frame-picker]:not([data-frame-booted])"));
  roots.forEach(function (r) { r.dataset.frameBooted = "1"; });

  function buildTileButton(tile) {
    var btn = document.createElement("button");
    btn.type = "button";
    btn.className = "frame-picker__tile";
    btn.setAttribute("data-frame-tile", "");
    btn.setAttribute("data-frame-handle", tile.key);
    btn.setAttribute("aria-pressed", "false");

    var thumb = document.createElement("span");
    thumb.className = "frame-picker__thumb";
    if (tile.image) {
      var img = document.createElement("img");
      img.src = tile.image;
      img.width = 66;
      img.height = 66;
      img.loading = "lazy";
      img.alt = tile.label;
      thumb.appendChild(img);
    }
    btn.appendChild(thumb);

    var label = document.createElement("span");
    label.className = "frame-picker__label";
    label.textContent = tile.label;
    btn.appendChild(label);

    var price = document.createElement("span");
    price.className = "frame-picker__price";
    price.setAttribute("data-frame-price", "");
    btn.appendChild(price);

    return btn;
  }

  var tiles = [];
  var noteEls = [];
  roots.forEach(function (r) {
    tiles = tiles.concat(Array.prototype.slice.call(r.querySelectorAll("[data-frame-tile]")));
    var n = r.querySelector("[data-frame-note]");
    if (n) noteEls.push(n);

    var slot = r.querySelector("[data-frame-tiles]");
    if (!slot) return;
    TILES.forEach(function (tile) {
      var btn = buildTileButton(tile);
      slot.parentNode.insertBefore(btn, slot);
      tiles.push(btn);
    });
  });

  function tileHandle(tile) { return tile.getAttribute("data-frame-handle") || ""; }

  function priceTextFor(key) {
    var resolved = resolveForSize(findTileByKey(key), STATE.size);
    if (!resolved || resolved.available === false) return t("sold_out", "Sold out");
    return "+" + (resolved.priceFormatted || "");
  }

  function renderTiles() {
    tiles.forEach(function (tile) {
      var key = tileHandle(tile);
      tile.setAttribute("aria-pressed", key === (STATE.selectedKey || "") ? "true" : "false");
      if (!key) return; // the "no frame" tile is never disabled or re-priced
      var resolved = resolveForSize(findTileByKey(key), STATE.size);
      tile.disabled = !resolved || resolved.available === false;
      var priceEl = tile.querySelector("[data-frame-price]");
      if (priceEl) priceEl.textContent = priceTextFor(key);
    });
  }

  // With a frame selected, prefer the combined "poster + frame = total" line (the client's
  // "no price on the button so adding a frame will not be apparent in the endprice" fix -- the
  // 8 standard Dawn product pages this picker also mounts on show the poster's price in Dawn's
  // own price block, never on the Add to cart button, so the frame's own footer note is the
  // only place a combined total can appear). Falls back to the plain selected_note whenever any
  // ingredient is missing -- an unknown poster price, a money_format with no recognised
  // placeholder, or a frame price that failed to resolve -- rather than ever show a partial
  // total or "NaN". The five canvas-builder sections compute and show their own totals already
  // (see flower-poster-section.js's updateCartPrice) and don't rely on this note for that.
  function renderNote() {
    if (!noteEls.length) return;
    var text;
    if (!STATE.selectedKey) {
      text = t("none_note", "Poster only — no frame added.");
    } else {
      var tile = findTileByKey(STATE.selectedKey);
      var resolved = resolveForSize(tile, STATE.size);
      var frameFormatted = (resolved && resolved.priceFormatted) || "";
      var totalText = null;
      if (STATE.posterPrice && resolved && resolved.available !== false && typeof resolved.price === "number" && frameFormatted && MONEY_FORMAT) {
        var posterFormatted = applyMoneyFormat(STATE.posterPrice, MONEY_FORMAT);
        var totalFormatted = applyMoneyFormat(STATE.posterPrice + resolved.price, MONEY_FORMAT);
        if (posterFormatted && totalFormatted) {
          totalText = t("total_note", "Poster {poster_price} + frame {frame_price} = {total_price}")
            .replace("{poster_price}", posterFormatted)
            .replace("{frame_price}", frameFormatted)
            .replace("{total_price}", totalFormatted);
        }
      }
      if (totalText) {
        text = totalText;
      } else {
        var template = t("selected_note", "Frame: {color} added (+{price})");
        text = template
          .replace("{color}", (tile && tile.label) || "")
          .replace("{price}", frameFormatted);
      }
    }
    noteEls.forEach(function (el) { el.textContent = text; });
  }

  function selectKey(key) {
    STATE.selectedKey = key || null;
    renderTiles();
    renderNote();
    notify();
  }

  tiles.forEach(function (tile) {
    tile.addEventListener("click", function () {
      if (tile.disabled) return;
      selectKey(tileHandle(tile));
    });
  });
  renderTiles();
  renderNote();

  // ============================================================
  // D) PUBLIC API -- setSize/getSelection/buildItems/onChange unchanged in shape from the
  // single-tile-per-product version; getSelection() still {variantId, colorLabel, price, size}.
  // setPosterPrice is new (2026-08-30, the cart-total upsell fix) -- it only feeds renderNote's
  // combined total and never affects getSelection/buildItems' bundle-building.
  // ============================================================
  function setSize(size) {
    // A real detected size always wins over the tag; an empty/undetected one (size switched to
    // something with no recognisable dimension, or the caller simply has nothing to report)
    // falls back to DEFAULT_SIZE rather than blanking the picker out from under a tagged
    // product. Neither present just means "" -- unknown, tiles stay disabled.
    STATE.size = size || DEFAULT_SIZE;
    // A colour with no available variant at the new size falls back to "no frame" -- never
    // leaves a disabled/sold-out tile selected.
    if (STATE.selectedKey) {
      var resolved = resolveForSize(findTileByKey(STATE.selectedKey), STATE.size);
      if (!resolved || resolved.available === false) STATE.selectedKey = null;
    }
    renderTiles();
    renderNote();
    notify();
  }

  // Called by product-form.js's onVariantIdChanged whenever the customer switches the poster's
  // own variant (size, and on some products also finish/style), so the footer's combined total
  // stays correct without re-selecting the frame. cents anything other than a finite number
  // (absent, NaN, undefined) resets to "unknown", which renderNote() treats the same as a
  // product with no frame picker context at all -- fall back to the plain selected_note.
  function setPosterPrice(cents) {
    STATE.posterPrice = (typeof cents === "number" && isFinite(cents) && cents > 0) ? cents : 0;
    renderNote();
  }

  function getSelection() {
    if (!STATE.selectedKey) return null;
    var tile = findTileByKey(STATE.selectedKey);
    var resolved = tile && resolveForSize(tile, STATE.size);
    if (!resolved) return null;
    return {
      variantId: resolved.id,
      colorLabel: tile.label,
      price: resolved.price,
      size: STATE.size
    };
  }

  // posterItem is the {id, quantity, properties} shape /cart/add.js expects. An optional
  // posterItem.title (the poster's product title -- only the caller knows it) is used to
  // build the frame's visible line-item property and is stripped before the poster entry
  // is returned, since it isn't a real cart property.
  function buildItems(posterItem) {
    if (!posterItem || typeof posterItem !== "object") return [posterItem];
    if (!STATE.selectedKey) return [posterItem];

    var tile = findTileByKey(STATE.selectedKey);
    if (!tile) return [posterItem];

    var posterProps = posterItem.properties || {};
    var size = posterProps._size || posterProps.size || STATE.size || "";
    var resolved = resolveForSize(tile, size);
    if (!resolved || resolved.available === false) return [posterItem];

    var bundleId = randomBundleId();

    var posterOut = {};
    for (var key in posterItem) {
      if (Object.prototype.hasOwnProperty.call(posterItem, key)) posterOut[key] = posterItem[key];
    }
    delete posterOut.title;
    posterOut.properties = {};
    for (var pKey in posterProps) {
      if (Object.prototype.hasOwnProperty.call(posterProps, pKey)) posterOut.properties[pKey] = posterProps[pKey];
    }
    posterOut.properties._bundle_id = bundleId;
    posterOut.properties._bundle_role = "poster";

    var forPropertyKey = t("for_property", "For poster");
    var frameOut = {
      id: resolved.id,
      quantity: posterItem.quantity || 1,
      properties: {}
    };
    frameOut.properties._bundle_id = bundleId;
    frameOut.properties._bundle_role = "frame";
    frameOut.properties._frame_size = size;
    frameOut.properties[forPropertyKey] = composeForLabel(posterItem.title, size);

    return [posterOut, frameOut];
  }

  function onChange(cb) {
    if (typeof cb === "function") listeners.push(cb);
  }

  window.TBPFrame = {
    VERSION: FRAME_VERSION,
    setSize: setSize,
    setPosterPrice: setPosterPrice,
    getSelection: getSelection,
    buildItems: buildItems,
    onChange: onChange
  };
})();
